<script setup lang="ts">
const view = useCurrentView()
const db = useDb()
const ecoleId = useCurrentEcoleId()
const currentUser = useCurrentUser()
const theme = useTheme()
const { ctxOpen, notifOpen } = useMenus()
const { barCount } = useTable()

const titleOverride = useTitleOverride()
const title = computed(() => titleOverride.value ?? viewTitle(view.value))
const annee = computed(() => currentAnnee())
const ecole = computed(() => findById('ecoles', ecoleId.value) || db.value.ecoles?.[0])
const visibleEcoles = computed(() => {
  const all = db.value.ecoles ?? []
  const allowedIds = currentUser.value?.ecoleIds
  return allowedIds ? all.filter(e => allowedIds.includes(e.id as string)) : all
})
const court = computed(() => {
  const p = ecoleParts(ecole.value)
  return ecole.value ? (p.sigle || p.nom) : '—'
})
const pending = computed(() => caissePendingCount())
const retard = computed(() => overdueStudentsCount())
const total = computed(() => pending.value + retard.value)
</script>

<template>
  <header class="topbar">
    <div class="bar-in">
      <h1
        id="bar-title"
        class="bar-title"
        :title="title"
      >
        {{ title }}
      </h1>
      <span
        id="bar-count"
        class="chip bar-count"
        :hidden="barCount === null"
        :title="barCount === null ? undefined : `${barCount} enregistrement${barCount > 1 ? 's' : ''}`"
      >{{ barCount }}</span>
      <div class="bar-right">
        <div class="search-box">
          <span class="ico"><AppIcon name="search" /></span>
          <input
            type="text"
            placeholder="Rechercher…"
            aria-label="Rechercher"
            disabled
          >
          <span class="kbd">Ctrl K</span>
        </div>
        <div class="ctx-wrap">
          <button
            id="ctx-chip"
            type="button"
            class="ctx-chip"
            aria-haspopup="true"
            :aria-expanded="ctxOpen ? 'true' : 'false'"
            title="École et année académique"
            @click="toggleCtxMenu($event)"
          >
            <span>{{ court }}</span><span class="ctx-sep">·</span><b>{{ annee ? annee.libelle : '—' }}</b><AppIcon name="chevron-down" />
          </button>
          <div
            id="ctx-menu"
            :class="['ctx-menu', { open: ctxOpen }]"
            role="menu"
          >
            <div class="ctx-title">
              École
            </div>
            <button
              v-for="e in visibleEcoles"
              :key="e.id"
              type="button"
              role="menuitem"
              :class="{ on: e.id === ecoleId }"
              @click="closeCtxMenu(); setCurrentEcole(e.id)"
            >
              <span class="ec"><span
                v-if="ecoleParts(e).sigle"
                class="ec-sigle"
              >{{ ecoleParts(e).sigle }}</span><span :class="ecoleParts(e).sigle ? 'ec-nom' : 'ec-sigle'">{{ ecoleParts(e).nom }}</span></span>
            </button>
            <button
              type="button"
              role="menuitem"
              @click="closeCtxMenu(); openForm('ecoles', ecoleId)"
            >
              <AppIcon
                name="pencil"
                extra="icon-sm"
              /> Modifier cette école
            </button>
            <div class="ctx-sepline" />
            <div class="ctx-title">
              Année académique
            </div>
            <div class="ctx-year">
              {{ annee ? annee.libelle : '—' }}
            </div>
          </div>
        </div>
        <button
          id="theme-toggle"
          class="icon-btn"
          title="Changer de thème"
          aria-label="Changer de thème"
          @click="toggleTheme()"
        >
          <AppIcon :name="theme === 'dark' ? 'sun' : 'moon'" />
        </button>
        <div
          id="notif-wrap"
          class="ctx-wrap"
        >
          <button
            id="notif-btn"
            class="icon-btn"
            title="Notifications"
            aria-label="Notifications"
            :aria-expanded="notifOpen ? 'true' : 'false'"
            @click="toggleNotifMenu($event)"
          >
            <AppIcon name="bell" /><span
              id="notif-badge"
              class="notif-badge"
              :hidden="total <= 0"
            >{{ total }}</span>
          </button>
          <div
            id="notif-menu"
            :class="['ctx-menu', { open: notifOpen }]"
            role="menu"
          >
            <div class="ctx-title">
              Notifications
            </div>
            <div
              v-if="total <= 0"
              style="padding:10px 12px; font-size:13.5px; color:var(--ink-faint);"
            >
              Aucune nouvelle notification.
            </div>
            <template v-else>
              <button
                v-if="pending > 0"
                type="button"
                role="menuitem"
                @click="closeNotifMenu(); goTo('report', 'caissePending')"
              >
                <AppIcon
                  name="inbox"
                  extra="icon-sm"
                /> {{ pending }} inscription{{ pending > 1 ? 's' : '' }} en attente de paiement
              </button>
              <button
                v-if="retard > 0"
                type="button"
                role="menuitem"
                @click="closeNotifMenu(); goTo('report', 'relances')"
              >
                <AppIcon
                  name="alert-triangle"
                  extra="icon-sm"
                /> {{ retard }} étudiant{{ retard > 1 ? 's' : '' }} en retard de paiement
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>
